import { useEffect, useRef } from "react";

import { useSaveableSectionsStore } from "../../stores/saveableSectionsStore";
import type { SaveableSection } from "./sectionSaveHandle";

/**
 * Registers a section with the shared save orchestrator while enabled.
 * `section`'s functions can change every render (they
 * close over component state) without churning the registry: only one stable
 * wrapper is ever registered, and it always delegates to the latest handlers
 * via a ref.
 * @param {string} id - Stable section id (e.g. "signers") — the merge key in the registry.
 * @param {SaveableSection} section - The section's validate/getPayload/onSaved/onSaveFailed handlers.
 * @param {boolean} enabled - Whether to participate in saves while retaining the caller's form state.
 */
export function useRegisterSaveableSection(id: string, section: SaveableSection, enabled = true): void {
  const sectionReference = useRef(section);
  sectionReference.current = section;

  const register = useSaveableSectionsStore((state) => state.register);
  const unregister = useSaveableSectionsStore((state) => state.unregister);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    register(id, {
      validate: () => sectionReference.current.validate(),
      getPayload: () => sectionReference.current.getPayload(),
      saveBlocker: () => sectionReference.current.saveBlocker?.(),
      onSaved: (metadata) => sectionReference.current.onSaved(metadata),
      onSaveFailed: (error) => sectionReference.current.onSaveFailed(error),
    });
    return () => unregister(id);
  }, [id, register, unregister, enabled]);
}
